import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import { useStores } from '../../../stores/useStores';
import { FilterType } from '../../../apiclients/api/types/FilterType';
import { UserManagementFilter } from '../../../stores/UserManagement/types';

import { FilterCheckbox } from './utils/types';
import { useColumnFilterValues } from './useColumnFilterValues';
import { BLANK_VALUE, INDETERMINATE, SELECT_ALL } from './utils/constants';

/**
 * @property columnKey - string; Column key to match data key
 * @property includedValues - string[]; Array of values to include to data (SELECT_ALL is not checked)
 * @property searchIsActive - boolean; If filter's value search is active do not hide all rows and apply filters accordingly (include/exclude)
 * @property selectAllChecked - boolean; SELECT_ALL checked state
 * @property setIncludedValues - (string) => void; Callback to change includedValues state
 * @property setSelectAllChecked - (boolean) => void; Callback to change SELECT_ALL state
 */
type Props = {
  columnKey: string;
  includedValues: Set<string>
  searchIsActive?: boolean
  selectAllChecked: boolean
  setIncludedValues: (value: Set<string>) => void
  setSelectAllChecked: (value: boolean) => void
};

export const useFilterValueList = ({
  columnKey,
  searchIsActive,
  includedValues,
  selectAllChecked,
  setIncludedValues,
  setSelectAllChecked
}: Props) => {
  const { t } = useTranslation();
  const { userManagementStore } = useStores();

  /**
   * Paginated filter values
   * Use observable store property to refetch data on filters change
   */
  const {
    data = [],
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useColumnFilterValues({
    key: columnKey,
    filters: userManagementStore.getColumnFilters(columnKey),
  });

  /**
   * Debounced Apply filter; Change observable state and refetch data
   */
  const applyFilter = useDebouncedCallback((filter: UserManagementFilter) => {
    userManagementStore.applyFilter(filter)
  }, 400)

  /**
   * Debounced Remove filter; Change observable state and refetch data
   */
  const removeFilter = useDebouncedCallback((filterKey: UserManagementFilter["key"]) => {
    userManagementStore.removeFilter(filterKey)
  }, 400)

  /**
   * Utility function to determine if list scrolled to a bottom
   */
  const isAtBottom = ({
    currentTarget,
  }: React.UIEvent<HTMLDivElement>): boolean => (
    currentTarget.scrollTop + 68 >=
    currentTarget.scrollHeight - currentTarget.clientHeight
  )

  /**
   * Fetch next page at bottom of the list
   */
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!isFetching && hasNextPage && isAtBottom(event)) {
      fetchNextPage()
    }
  };

  const handleFilterChange = useCallback((values: Set<string>, selectAll: boolean) => {
    if (values.size) {
      applyFilter.cancel()
      applyFilter({
        key: columnKey,
        value: [...values],
        exclude: selectAll,
        filterType: FilterType.AutoComplete
      })
      userManagementStore.removeUncheckedColumn(columnKey)
    } else {
      // if selection is empty
      // clear current filter and toggle hide all rows
      removeFilter.cancel()
      removeFilter(columnKey)
      if (selectAll) {
        userManagementStore.removeUncheckedColumn(columnKey)
      } else {
        userManagementStore.addUncheckedColumn(columnKey)
      }
    }
  }, [applyFilter, columnKey, removeFilter, userManagementStore])

  const handleCheckboxChange = useCallback((item: FilterCheckbox) => {
    if (item.value === SELECT_ALL) {
      // if search is active
      // include all selection
      // toggle select all back
      if (searchIsActive) {
        setIncludedValues(new Set(data))
        setSelectAllChecked(item.isChecked)
        handleFilterChange(new Set(data), item.isChecked)
      } else {
        // if search is not active
        // remove all selection
        // and toggle select all
        setIncludedValues(new Set())
        setSelectAllChecked(!item.isChecked)
        handleFilterChange(new Set(), !item.isChecked)
      }
      return
    }
    // toggle selection
    const newInclude = new Set(includedValues)
    if (newInclude.has(item.value)) {
      newInclude.delete(item.value)
    } else {
      newInclude.add(item.value)
    }
    setIncludedValues(newInclude)
    handleFilterChange(newInclude, selectAllChecked)
  }, [data, handleFilterChange, includedValues, searchIsActive, selectAllChecked, setIncludedValues, setSelectAllChecked])

  const getCheckedState = useCallback((value: string) => {
    // if select all true or INDETERMINATE
    // check if value is not in a set
    if (selectAllChecked) {
      return !includedValues.has(value)
    }
    // if select all is false
    // check if value is in a set
    return includedValues.has(value)
  }, [includedValues, selectAllChecked])

  const getSelectAllChecked = useCallback(() => {
    if (includedValues.size) {
      const selected = data.filter((v) => includedValues.has(v))
      if (searchIsActive) {
        // If search is active and data is not in selection return select all state
        if (selected.length === 0) {
          return selectAllChecked
        }
      }
      // If all values from data are select
      // toggle select all state
      if (selected.length === data.length) {
        return !selectAllChecked;
      }
      // There is checked values return INDETERMINATE state
      return INDETERMINATE
    }
    // No checked items return select all state
    return selectAllChecked
  }, [data, includedValues, searchIsActive, selectAllChecked])

  // Map data to create Filter items
  const filterItems = useMemo(() => {
    if (userManagementStore.hideAllRows() && !userManagementStore.uncheckedColumns.has(columnKey)) {
      return [];
    }
    return data.map((value) => ({
      value,
      label: value === BLANK_VALUE ? t("blankValue") : value,
      isChecked: getCheckedState(value)
    }))
  }, [columnKey, data, getCheckedState, t, userManagementStore])

  // SELECT_ALL item
  const selectAll = useMemo(() => ({
    value: SELECT_ALL,
    label: t("cellFilterSelectAll"),
    isChecked: getSelectAllChecked()
  }), [getSelectAllChecked, t])

  return {
    filterItems: [selectAll, ...filterItems],
    handleScroll,
    handleCheckboxChange,
    isFetching
  }
}
